import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import './ResumeBuilder.css';

const STEPS = [
  'Dados pessoais',
  'Resumo e competências',
  'Experiências',
  'Formação',
  'Certificações, idiomas e projetos',
  'Revisão e geração',
];

const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const LEVELS = ['Ensino fundamental', 'Ensino médio', 'Curso técnico', 'Tecnólogo', 'Graduação', 'Pós-graduação', 'Mestrado', 'Doutorado'];
export const FIELD_LIMITS = {
  fullName: 100,
  professionalTitle: 80,
  city: 60,
  email: 254,
  url: 300,
  area: 80,
  knowledge: 300,
  differentiators: 300,
  tools: 200,
  opportunity: 120,
  professionalSummary: 600,
  skill: 50,
  role: 80,
  company: 100,
  location: 80,
  achievement: 300,
  course: 100,
  institution: 100,
  certification: 120,
  credentialCode: 80,
  language: 40,
  projectName: 100,
  projectRole: 80,
  projectActivities: 600,
};
export const EDUCATION_STATUS_LABELS = {
  cursando: 'Cursando',
  concluido: 'Concluído',
  interrompido: 'Interrompido',
};
export const LANGUAGE_LEVEL_LABELS = {
  basico: 'Básico',
  intermediario: 'Intermediário',
  avancado: 'Avançado',
  fluente: 'Fluente',
  nativo: 'Nativo',
};
const LANGUAGE_LEVELS = Object.entries(LANGUAGE_LEVEL_LABELS);
const SKILL_SUGGESTIONS = ['Pacote Office', 'Atendimento ao cliente', 'Gestão de documentos', 'Elaboração de relatórios', 'Organização', 'Comunicação', 'Trabalho em equipe', 'Excel', 'Planejamento', 'Resolução de problemas'];

const blankResume = () => ({
  title: 'Meu currículo',
  current_step: 1,
  no_experience: false,
  contact: { full_name: '', professional_title: '', city: '', state: '', phone: '', email: '', linkedin: '', portfolio: '' },
  area: '',
  years_experience: '',
  knowledge: '',
  differentiators: '',
  tools: '',
  opportunity: '',
  professional_summary: '',
  skills: [],
  experiences: [],
  educations: [],
  abraliber_certificates: [],
  available_abraliber_certificates: [],
  external_certifications: [],
  languages: [],
  projects: [],
  settings: { hidden_sections: [], optional_order: ['certifications', 'languages', 'projects'], truthful_confirmed: false },
});

const normalize = (data) => ({
  ...blankResume(),
  ...data,
  contact: { ...blankResume().contact, ...(data?.contact || {}) },
  skills: data?.skills || [],
  experiences: data?.experiences || [],
  educations: data?.educations || [],
  abraliber_certificates: data?.abraliber_certificates || [],
  available_abraliber_certificates: data?.available_abraliber_certificates || [],
  external_certifications: data?.external_certifications || [],
  languages: data?.languages || [],
  projects: data?.projects || [],
  settings: { ...blankResume().settings, ...(data?.settings || {}) },
});

const payloadOf = (resume) => {
  const {
    id, user_id, version_of_id, status, is_primary, content_hash, confirmed_at,
    generated_at, created_at, updated_at, version_count, available_abraliber_certificates,
    ...payload
  } = resume;
  return payload;
};

const Field = ({ label, error, children, hint, className = '' }) => (
  <label className={`resume-field ${className}`}>
    <span>{label}</span>
    {children}
    {hint && <small>{hint}</small>}
    {error && <small className="resume-field-error" role="alert">{error}</small>}
  </label>
);

const move = (items, index, direction) => {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const copy = [...items];
  [copy[index], copy[target]] = [copy[target], copy[index]];
  return copy;
};

const formatMonth = (value) => {
  if (!value) return '';
  const [year, month] = value.split('-');
  const labels = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
  return `${labels[Number(month) - 1] || month} de ${year}`;
};

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const validUrl = (value) => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch (_) {
    return false;
  }
};

export const ResumePreview = ({ resume }) => {
  const official = (resume.available_abraliber_certificates || []).filter((item) => resume.abraliber_certificates.includes(item.curso_id));
  const hidden = resume.settings.hidden_sections || [];
  const optionalOrder = resume.settings.optional_order || ['certifications', 'languages', 'projects'];
  const contact = resume.contact;
  const contactLine = [
    [contact.city, contact.state].filter(Boolean).join(', '),
    contact.phone,
    contact.email,
    contact.linkedin,
    contact.portfolio,
  ].filter(Boolean).join(' | ');

  const optional = {
    certifications: !hidden.includes('certifications') && (official.length + resume.external_certifications.length > 0) && (
      <section key="certifications">
        <h3>Certificações</h3>
        {[...official, ...resume.external_certifications].map((item, index) => (
          <div className="resume-preview-entry" key={`${item.name}-${index}`}>
            <strong>{item.name} | {item.institution}{item.year ? ` | ${item.year}` : ''}{item.hours ? ` | ${item.hours} horas` : ''}</strong>
            {(item.credential_code || item.validation_url) && (
              <small>
                {item.credential_code ? `Credencial: ${item.credential_code}` : ''}
                {item.credential_code && item.validation_url ? ' | ' : ''}
                {item.validation_url ? `Validação: ${item.validation_url}` : ''}
              </small>
            )}
          </div>
        ))}
      </section>
    ),
    languages: !hidden.includes('languages') && resume.languages.length > 0 && (
      <section key="languages">
        <h3>Idiomas</h3>
        <p>{resume.languages.map((item) => `${item.language}: ${LANGUAGE_LEVEL_LABELS[item.level] || 'Nível não informado'}`).join(' | ')}</p>
      </section>
    ),
    projects: !hidden.includes('projects') && resume.projects.length > 0 && (
      <section key="projects">
        <h3>Projetos e trabalho voluntário</h3>
        {resume.projects.map((item, index) => (
          <div className="resume-preview-entry" key={`${item.name}-${index}`}>
            <strong>{item.name} | {item.role}</strong>
            {(item.start_month || item.end_month) && <small>{formatMonth(item.start_month)} – {item.is_current ? 'Atual' : formatMonth(item.end_month)}</small>}
            {item.activities && <p>{item.activities}</p>}
          </div>
        ))}
      </section>
    ),
  };

  return (
    <article className="resume-paper" aria-label="Prévia do currículo">
      <h2>{contact.full_name || 'SEU NOME COMPLETO'}</h2>
      <h4>{contact.professional_title || 'Cargo ou objetivo profissional'}</h4>
      {contactLine && <p className="resume-preview-contact">{contactLine}</p>}
      {resume.professional_summary && <section><h3>Resumo profissional</h3><p>{resume.professional_summary}</p></section>}
      {resume.skills.length > 0 && <section><h3>Competências</h3><p>{resume.skills.join(' • ')}</p></section>}
      {!resume.no_experience && resume.experiences.length > 0 && (
        <section>
          <h3>Experiência profissional</h3>
          {resume.experiences.map((item, index) => (
            <div className="resume-preview-entry" key={`${item.company}-${index}`}>
              <strong>{item.role} | {item.company}</strong>
              <small>{formatMonth(item.start_month)} – {item.is_current ? 'Atual' : formatMonth(item.end_month)}{item.location ? ` | ${item.location}` : ''}</small>
              {item.achievements?.length > 0 && <ul>{item.achievements.map((text, achievementIndex) => <li key={achievementIndex}>{text}</li>)}</ul>}
            </div>
          ))}
        </section>
      )}
      {resume.educations.length > 0 && (
        <section>
          <h3>Formação acadêmica</h3>
          {resume.educations.map((item, index) => (
            <div className="resume-preview-entry" key={`${item.institution}-${index}`}>
              <strong>{item.course} | {item.institution}</strong>
              <small>{item.level} | {EDUCATION_STATUS_LABELS[item.status] || 'Situação não informada'}</small>
            </div>
          ))}
        </section>
      )}
      {optionalOrder.map((key) => optional[key])}
    </article>
  );
};

const ResumeBuilder = () => {
  const [resume, setResume] = useState(blankResume);
  const [resumes, setResumes] = useState([]);
  const [step, setStep] = useState(1);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState('Carregando...');
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState('');
  const [skillDraft, setSkillDraft] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const lastSaved = useRef('');
  const saveTimer = useRef(null);

  const update = (key, value) => setResume((current) => ({ ...current, [key]: value }));
  const updateContact = (key, value) => setResume((current) => ({ ...current, contact: { ...current.contact, [key]: value } }));
  const updateArrayItem = (key, index, field, value) => setResume((current) => ({
    ...current,
    [key]: current[key].map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
  }));
  const errorFor = (key) => Array.isArray(errors[key]) ? errors[key][0] : errors[key];

  const refreshList = useCallback(async () => {
    const response = await apiService.resumes.list();
    setResumes(response.data.resumes || []);
    return response.data.resumes || [];
  }, []);

  const openResume = useCallback(async (id) => {
    setReady(false);
    setSaveState('Carregando...');
    const response = await apiService.resumes.get(id);
    const next = normalize(response.data);
    setResume(next);
    setStep(next.current_step || 1);
    lastSaved.current = JSON.stringify(payloadOf(next));
    setReady(true);
    setSaveState('Rascunho salvo');
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const list = await refreshList();
        if (list.length) {
          await openResume((list.find((item) => item.is_primary) || list[0]).id);
        } else {
          const response = await apiService.resumes.create({});
          await refreshList();
          const next = normalize(response.data);
          setResume(next);
          setStep(1);
          lastSaved.current = JSON.stringify(payloadOf(next));
          setReady(true);
          setSaveState('Rascunho salvo');
        }
      } catch (error) {
        setNotice(
          error.response?.status === 403
            ? 'O currículo profissional não está disponível para esta conta.'
            : 'Não foi possível carregar o editor de currículo.',
        );
        setSaveState('Falha ao carregar');
      }
    };
    load();
  }, [openResume, refreshList]);

  const saveNow = useCallback(async (source = resume, showNotice = false) => {
    if (!source.id) return null;
    const payload = payloadOf({ ...source, current_step: step });
    const serialized = JSON.stringify(payload);
    if (serialized === lastSaved.current) {
      if (showNotice) setNotice('O rascunho já está atualizado.');
      return source;
    }
    setSaveState('Salvando...');
    try {
      const response = await apiService.resumes.update(source.id, payload);
      lastSaved.current = serialized;
      setSaveState('Salvo automaticamente');
      if (showNotice) setNotice('Rascunho salvo com sucesso.');
      refreshList();
      return response.data.resume;
    } catch (error) {
      setSaveState('Falha ao salvar — dados preservados');
      setErrors(error.response?.data?.errors || {});
      if (showNotice) setNotice('Não foi possível salvar. Confira os campos destacados.');
      throw error;
    }
  }, [refreshList, resume, step]);

  useEffect(() => {
    if (!ready || !resume.id) return undefined;
    const serialized = JSON.stringify(payloadOf({ ...resume, current_step: step }));
    if (serialized === lastSaved.current) return undefined;
    setSaveState('Alterações não salvas');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveNow(resume).catch(() => {}), 900);
    return () => clearTimeout(saveTimer.current);
  }, [ready, resume, saveNow, step]);

  useEffect(() => {
    const warn = (event) => {
      const dirty = ready && JSON.stringify(payloadOf({ ...resume, current_step: step })) !== lastSaved.current;
      if (dirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [ready, resume, step]);

  const validateStep = (target = step) => {
    const nextErrors = {};
    if (target === 1) {
      if (!resume.contact.full_name.trim()) nextErrors['contact.full_name'] = 'Informe seu nome completo.';
      else if (resume.contact.full_name.length > FIELD_LIMITS.fullName) nextErrors['contact.full_name'] = `Use no máximo ${FIELD_LIMITS.fullName} caracteres.`;
      if (!resume.contact.professional_title.trim()) nextErrors['contact.professional_title'] = 'Informe o cargo ou objetivo.';
      else if (resume.contact.professional_title.length > FIELD_LIMITS.professionalTitle) nextErrors['contact.professional_title'] = `Use no máximo ${FIELD_LIMITS.professionalTitle} caracteres.`;
      if (!resume.contact.city.trim()) nextErrors['contact.city'] = 'Informe a cidade.';
      else if (resume.contact.city.length > FIELD_LIMITS.city) nextErrors['contact.city'] = `Use no máximo ${FIELD_LIMITS.city} caracteres.`;
      if (!resume.contact.state) nextErrors['contact.state'] = 'Selecione a UF.';
      if (!/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(resume.contact.phone)) nextErrors['contact.phone'] = 'Use o formato (00) 00000-0000.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resume.contact.email)) nextErrors['contact.email'] = 'Informe um e-mail válido.';
      else if (resume.contact.email.length > FIELD_LIMITS.email) nextErrors['contact.email'] = `Use no máximo ${FIELD_LIMITS.email} caracteres.`;
      if (!validUrl(resume.contact.linkedin)) nextErrors['contact.linkedin'] = 'Informe uma URL completa e válida.';
      else if ((resume.contact.linkedin || '').length > FIELD_LIMITS.url) nextErrors['contact.linkedin'] = `Use no máximo ${FIELD_LIMITS.url} caracteres.`;
      if (!validUrl(resume.contact.portfolio)) nextErrors['contact.portfolio'] = 'Informe uma URL completa e válida.';
      else if ((resume.contact.portfolio || '').length > FIELD_LIMITS.url) nextErrors['contact.portfolio'] = `Use no máximo ${FIELD_LIMITS.url} caracteres.`;
    }
    if (target === 2) {
      if (!resume.professional_summary.trim()) nextErrors.professional_summary = 'Escreva ou gere um resumo profissional.';
      else if (resume.professional_summary.length > FIELD_LIMITS.professionalSummary) nextErrors.professional_summary = `Use no máximo ${FIELD_LIMITS.professionalSummary} caracteres.`;
      [['area', FIELD_LIMITS.area], ['knowledge', FIELD_LIMITS.knowledge], ['differentiators', FIELD_LIMITS.differentiators], ['tools', FIELD_LIMITS.tools], ['opportunity', FIELD_LIMITS.opportunity]].forEach(([key, limit]) => {
        if ((resume[key] || '').length > limit) nextErrors[key] = `Use no máximo ${limit} caracteres.`;
      });
      if (resume.skills.some((skill) => skill.length > FIELD_LIMITS.skill)) nextErrors.skills = `Cada competência deve ter no máximo ${FIELD_LIMITS.skill} caracteres.`;
    }
    if (target === 3 && !resume.no_experience) {
      if (!resume.experiences.length) nextErrors.experiences = 'Adicione uma experiência ou marque a opção sem experiência.';
      resume.experiences.forEach((item, index) => {
        if (!item.role?.trim() || !item.company?.trim() || !item.start_month) nextErrors.experiences = 'Preencha cargo, empresa e início em todas as experiências.';
        if ((item.role || '').length > FIELD_LIMITS.role) nextErrors[`experiences.${index}.role`] = `Use no máximo ${FIELD_LIMITS.role} caracteres.`;
        if ((item.company || '').length > FIELD_LIMITS.company) nextErrors[`experiences.${index}.company`] = `Use no máximo ${FIELD_LIMITS.company} caracteres.`;
        if ((item.location || '').length > FIELD_LIMITS.location) nextErrors[`experiences.${index}.location`] = `Use no máximo ${FIELD_LIMITS.location} caracteres.`;
        (item.achievements || []).forEach((achievement, achievementIndex) => {
          if (achievement.length > FIELD_LIMITS.achievement) nextErrors[`experiences.${index}.achievements.${achievementIndex}`] = `Use no máximo ${FIELD_LIMITS.achievement} caracteres.`;
        });
        if (!item.is_current && !item.end_month) nextErrors[`experiences.${index}.end_month`] = 'Informe o término ou marque como trabalho atual.';
        if (item.end_month && item.start_month && item.end_month < item.start_month) nextErrors[`experiences.${index}.end_month`] = 'O término deve ser posterior ao início.';
      });
    }
    if (target === 4) {
      if (!resume.educations.length) nextErrors.educations = 'Adicione ao menos uma formação.';
      if (resume.educations.some((item) => !item.level || !item.course?.trim() || !item.institution?.trim() || !item.status)) nextErrors.educations = 'Preencha nível, curso, instituição e situação em todas as formações.';
      resume.educations.forEach((item, index) => {
        if ((item.course || '').length > FIELD_LIMITS.course) nextErrors[`educations.${index}.course`] = `Use no máximo ${FIELD_LIMITS.course} caracteres.`;
        if ((item.institution || '').length > FIELD_LIMITS.institution) nextErrors[`educations.${index}.institution`] = `Use no máximo ${FIELD_LIMITS.institution} caracteres.`;
      });
    }
    if (target === 5) {
      const incompleteCertificate = resume.external_certifications.some((item) => !item.name?.trim() || !item.institution?.trim() || !item.year || !validUrl(item.validation_url));
      const incompleteLanguage = resume.languages.some((item) => !item.language?.trim() || !item.level);
      const duplicateLanguages = new Set(resume.languages.map((item) => item.language?.trim().toLowerCase())).size !== resume.languages.length;
      const incompleteProject = resume.projects.some((item) => !item.name?.trim() || !item.role?.trim() || !validUrl(item.link) || (item.end_month && item.start_month && item.end_month < item.start_month));
      if (incompleteCertificate || incompleteLanguage || duplicateLanguages || incompleteProject) nextErrors.extras = 'Revise os itens adicionados: campos principais, datas, URLs e idiomas não duplicados.';
      resume.external_certifications.forEach((item, index) => {
        if ((item.name || '').length > FIELD_LIMITS.certification) nextErrors[`external_certifications.${index}.name`] = `Use no máximo ${FIELD_LIMITS.certification} caracteres.`;
        if ((item.institution || '').length > FIELD_LIMITS.institution) nextErrors[`external_certifications.${index}.institution`] = `Use no máximo ${FIELD_LIMITS.institution} caracteres.`;
        if ((item.credential_code || '').length > FIELD_LIMITS.credentialCode) nextErrors[`external_certifications.${index}.credential_code`] = `Use no máximo ${FIELD_LIMITS.credentialCode} caracteres.`;
        if ((item.validation_url || '').length > FIELD_LIMITS.url) nextErrors[`external_certifications.${index}.validation_url`] = `Use no máximo ${FIELD_LIMITS.url} caracteres.`;
      });
      resume.languages.forEach((item, index) => {
        if ((item.language || '').length > FIELD_LIMITS.language) nextErrors[`languages.${index}.language`] = `Use no máximo ${FIELD_LIMITS.language} caracteres.`;
      });
      resume.projects.forEach((item, index) => {
        if ((item.name || '').length > FIELD_LIMITS.projectName) nextErrors[`projects.${index}.name`] = `Use no máximo ${FIELD_LIMITS.projectName} caracteres.`;
        if ((item.role || '').length > FIELD_LIMITS.projectRole) nextErrors[`projects.${index}.role`] = `Use no máximo ${FIELD_LIMITS.projectRole} caracteres.`;
        if ((item.activities || '').length > FIELD_LIMITS.projectActivities) nextErrors[`projects.${index}.activities`] = `Use no máximo ${FIELD_LIMITS.projectActivities} caracteres.`;
        if ((item.link || '').length > FIELD_LIMITS.url) nextErrors[`projects.${index}.link`] = `Use no máximo ${FIELD_LIMITS.url} caracteres.`;
      });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = async () => {
    if (!validateStep()) return;
    const next = Math.min(6, step + 1);
    setStep(next);
    update('current_step', next);
    try { await saveNow({ ...resume, current_step: next }); } catch (_) {}
  };

  const addSkill = (value = skillDraft) => {
    const clean = value.trim();
    if (!clean || clean.length > FIELD_LIMITS.skill || resume.skills.some((item) => item.toLowerCase() === clean.toLowerCase()) || resume.skills.length >= 10) return;
    update('skills', [...resume.skills, clean]);
    setSkillDraft('');
  };

  const suggestSummary = async () => {
    setBusy(true);
    setNotice('');
    try {
      const response = await apiService.resumes.suggestSummary({
        area: resume.area,
        years_experience: resume.years_experience === '' ? null : Number(resume.years_experience),
        knowledge: resume.knowledge,
        differentiators: resume.differentiators,
        tools: resume.tools,
        opportunity: resume.opportunity,
      });
      update('professional_summary', response.data.suggestion);
      setNotice('Sugestão criada. Revise e edite antes de confirmar.');
    } catch (_) {
      setNotice('Preencha os campos auxiliares para gerar uma sugestão.');
    } finally {
      setBusy(false);
    }
  };

  const createNew = async () => {
    setBusy(true);
    try {
      await saveNow(resume);
      const response = await apiService.resumes.create({ title: `Currículo ${resumes.length + 1}` });
      await refreshList();
      const next = normalize(response.data);
      setResume(next);
      setStep(1);
      lastSaved.current = JSON.stringify(payloadOf(next));
      setReady(true);
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    if (!resume.id) return;
    setBusy(true);
    try {
      await saveNow(resume);
      const response = await apiService.resumes.duplicate(resume.id);
      await refreshList();
      const next = normalize(response.data);
      setResume(next);
      setStep(1);
      lastSaved.current = JSON.stringify(payloadOf(next));
      setNotice('Nova versão criada a partir deste currículo.');
    } finally {
      setBusy(false);
    }
  };

  const removeResume = async () => {
    if (!resume.id || !window.confirm('Excluir esta versão do currículo? Esta ação não pode ser desfeita.')) return;
    setBusy(true);
    try {
      await apiService.resumes.delete(resume.id);
      const list = await refreshList();
      if (list.length) await openResume(list[0].id);
      else await createNew();
    } finally {
      setBusy(false);
    }
  };

  const makePrimary = async () => {
    await apiService.resumes.makePrimary(resume.id);
    update('is_primary', true);
    await refreshList();
    setNotice('Este é agora o currículo principal.');
  };

  const download = async (format) => {
    if (![1, 2, 3, 4].every(validateStep) || !resume.settings.truthful_confirmed) {
      setStep(6);
      setNotice('Conclua os campos obrigatórios e confirme a veracidade das informações.');
      return;
    }
    setBusy(true);
    try {
      await saveNow(resume);
      const response = await apiService.resumes.download(resume.id, format);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Curriculo_${(resume.contact.full_name || 'Abraliber').replace(/\s+/g, '_')}.${format}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setNotice(`Currículo ${format.toUpperCase()} gerado com sucesso.`);
      refreshList();
    } catch (error) {
      const message = error.response?.data instanceof Blob
        ? 'Não foi possível gerar o arquivo. Revise os campos obrigatórios.'
        : error.response?.data?.message;
      setNotice(message || 'Não foi possível gerar o arquivo.');
    } finally {
      setBusy(false);
    }
  };

  const sectionsComplete = useMemo(() => [
    Boolean(resume.contact.full_name && resume.contact.professional_title && resume.contact.city && resume.contact.state && resume.contact.phone && resume.contact.email),
    Boolean(resume.professional_summary && resume.skills.length),
    Boolean(resume.no_experience || resume.experiences.length),
    Boolean(resume.educations.length),
    Boolean(resume.abraliber_certificates.length || resume.external_certifications.length || resume.languages.length || resume.projects.length),
  ], [resume]);

  const personalStep = (
    <>
      <div className="resume-panel-heading"><span>1</span><div><h2>Dados pessoais</h2><p>Inclua somente dados profissionais relevantes.</p></div></div>
      <div className="resume-form-grid">
        <Field label="Nome completo *" error={errors['contact.full_name']}><input value={resume.contact.full_name} onChange={(e) => updateContact('full_name', e.target.value)} maxLength={FIELD_LIMITS.fullName} /></Field>
        <Field label="Cargo ou objetivo profissional *" error={errors['contact.professional_title']}><input value={resume.contact.professional_title} onChange={(e) => updateContact('professional_title', e.target.value)} maxLength={FIELD_LIMITS.professionalTitle} /></Field>
        <Field label="Cidade *" error={errors['contact.city']}><input value={resume.contact.city} onChange={(e) => updateContact('city', e.target.value)} maxLength={FIELD_LIMITS.city} /></Field>
        <Field label="Estado (UF) *" error={errors['contact.state']}><select value={resume.contact.state} onChange={(e) => updateContact('state', e.target.value)}><option value="">Selecione</option>{UFS.map((uf) => <option key={uf}>{uf}</option>)}</select></Field>
        <Field label="Telefone *" error={errors['contact.phone']}><input value={resume.contact.phone} onChange={(e) => updateContact('phone', formatPhone(e.target.value))} placeholder="(11) 99999-9999" maxLength="15" inputMode="tel" /></Field>
        <Field label="E-mail profissional *" error={errors['contact.email']}><input type="email" value={resume.contact.email} onChange={(e) => updateContact('email', e.target.value)} maxLength={FIELD_LIMITS.email} /></Field>
        <Field label="LinkedIn" error={errors['contact.linkedin']} hint="Use uma URL completa, incluindo https://"><input type="url" value={resume.contact.linkedin || ''} onChange={(e) => updateContact('linkedin', e.target.value)} placeholder="https://linkedin.com/in/seuperfil" maxLength={FIELD_LIMITS.url} /></Field>
        <Field label="Portfólio ou site" error={errors['contact.portfolio']}><input type="url" value={resume.contact.portfolio || ''} onChange={(e) => updateContact('portfolio', e.target.value)} placeholder="https://seusite.com.br" maxLength={FIELD_LIMITS.url} /></Field>
      </div>
      <div className="resume-info">Para sua privacidade e compatibilidade com recrutadores, o currículo não inclui foto, CPF, RG, idade, endereço completo ou estado civil.</div>
    </>
  );

  const summaryStep = (
    <>
      <div className="resume-panel-heading"><span>2</span><div><h2>Resumo e competências</h2><p>Use somente informações verdadeiras; a sugestão nunca inventa experiências ou métricas.</p></div></div>
      <div className="resume-form-grid">
        <Field label="Área de atuação" error={errorFor('area')}><input value={resume.area || ''} onChange={(e) => update('area', e.target.value)} maxLength={FIELD_LIMITS.area} /></Field>
        <Field label="Anos de experiência"><input type="number" min="0" max="60" value={resume.years_experience ?? ''} onChange={(e) => update('years_experience', e.target.value)} /></Field>
        <Field label="Principais conhecimentos" error={errorFor('knowledge')}><input value={resume.knowledge || ''} onChange={(e) => update('knowledge', e.target.value)} maxLength={FIELD_LIMITS.knowledge} /></Field>
        <Field label="Diferenciais profissionais" error={errorFor('differentiators')}><input value={resume.differentiators || ''} onChange={(e) => update('differentiators', e.target.value)} maxLength={FIELD_LIMITS.differentiators} /></Field>
        <Field label="Ferramentas ou sistemas" error={errorFor('tools')}><input value={resume.tools || ''} onChange={(e) => update('tools', e.target.value)} maxLength={FIELD_LIMITS.tools} /></Field>
        <Field label="Oportunidade procurada" error={errorFor('opportunity')}><input value={resume.opportunity || ''} onChange={(e) => update('opportunity', e.target.value)} maxLength={FIELD_LIMITS.opportunity} /></Field>
      </div>
      <button type="button" className="resume-secondary" disabled={busy} onClick={suggestSummary}>Gerar sugestão de resumo</button>
      <Field label={`Resumo profissional * (${(resume.professional_summary || '').length}/${FIELD_LIMITS.professionalSummary})`} error={errorFor('professional_summary')}>
        <textarea rows="6" value={resume.professional_summary || ''} onChange={(e) => update('professional_summary', e.target.value)} maxLength={FIELD_LIMITS.professionalSummary} />
      </Field>
      <div className="resume-subsection">
        <h3>Competências <small>Recomendadas: de 5 a 10</small></h3>
        {errorFor('skills') && <p className="resume-field-error">{errorFor('skills')}</p>}
        <div className="resume-inline-add"><input value={skillDraft} onChange={(e) => setSkillDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }} placeholder="Digite uma competência" maxLength={FIELD_LIMITS.skill} /><button type="button" onClick={() => addSkill()}>Adicionar</button></div>
        <div className="resume-suggestions">{SKILL_SUGGESTIONS.filter((item) => !resume.skills.includes(item)).slice(0, 6).map((item) => <button type="button" key={item} onClick={() => addSkill(item)}>+ {item}</button>)}</div>
        <div className="resume-tags">{resume.skills.map((skill, index) => <span key={skill}><button type="button" aria-label={`Mover ${skill} para cima`} onClick={() => update('skills', move(resume.skills, index, -1))}>↑</button>{skill}<button type="button" aria-label={`Remover ${skill}`} onClick={() => update('skills', resume.skills.filter((_, itemIndex) => itemIndex !== index))}>×</button></span>)}</div>
      </div>
    </>
  );

  const experienceStep = (
    <>
      <div className="resume-panel-heading"><span>3</span><div><h2>Experiência profissional</h2><p>Cadastre da mais recente para a mais antiga.</p></div></div>
      <label className="resume-check"><input type="checkbox" checked={resume.no_experience} onChange={(e) => update('no_experience', e.target.checked)} /> Ainda não tenho experiência profissional</label>
      {errors.experiences && <p className="resume-field-error">{errors.experiences}</p>}
      {!resume.no_experience && resume.experiences.map((item, index) => (
        <div className="resume-repeat-card" key={item.id || index}>
          <div className="resume-repeat-title"><strong>Experiência {index + 1}</strong><div><button type="button" onClick={() => update('experiences', move(resume.experiences, index, -1))}>↑</button><button type="button" onClick={() => update('experiences', move(resume.experiences, index, 1))}>↓</button><button type="button" className="danger" onClick={() => window.confirm('Excluir esta experiência?') && update('experiences', resume.experiences.filter((_, i) => i !== index))}>Excluir</button></div></div>
          <div className="resume-form-grid">
            <Field label="Cargo *" error={errorFor(`experiences.${index}.role`)}><input value={item.role || ''} onChange={(e) => updateArrayItem('experiences', index, 'role', e.target.value)} maxLength={FIELD_LIMITS.role} /></Field>
            <Field label="Empresa *" error={errorFor(`experiences.${index}.company`)}><input value={item.company || ''} onChange={(e) => updateArrayItem('experiences', index, 'company', e.target.value)} maxLength={FIELD_LIMITS.company} /></Field>
            <Field label="Cidade e estado" error={errorFor(`experiences.${index}.location`)}><input value={item.location || ''} onChange={(e) => updateArrayItem('experiences', index, 'location', e.target.value)} maxLength={FIELD_LIMITS.location} /></Field>
            <Field label="Início *"><input type="month" value={item.start_month || ''} onChange={(e) => updateArrayItem('experiences', index, 'start_month', e.target.value)} /></Field>
            <Field label="Término" error={errors[`experiences.${index}.end_month`]}><input type="month" disabled={item.is_current} value={item.end_month || ''} onChange={(e) => updateArrayItem('experiences', index, 'end_month', e.target.value)} /></Field>
            <label className="resume-check align-end"><input type="checkbox" checked={Boolean(item.is_current)} onChange={(e) => { updateArrayItem('experiences', index, 'is_current', e.target.checked); if (e.target.checked) updateArrayItem('experiences', index, 'end_month', ''); }} /> Trabalho atualmente nesta empresa</label>
          </div>
          <h4>Principais atividades e resultados <small>(de 2 a 5 recomendados)</small></h4>
          {(item.achievements || []).map((achievement, achievementIndex) => (
            <div key={achievementIndex}><div className="resume-inline-add"><input value={achievement} onChange={(e) => updateArrayItem('experiences', index, 'achievements', item.achievements.map((value, i) => i === achievementIndex ? e.target.value : value))} maxLength={FIELD_LIMITS.achievement} /><button type="button" className="danger" onClick={() => updateArrayItem('experiences', index, 'achievements', item.achievements.filter((_, i) => i !== achievementIndex))}>Remover</button></div>{errorFor(`experiences.${index}.achievements.${achievementIndex}`) && <p className="resume-field-error">{errorFor(`experiences.${index}.achievements.${achievementIndex}`)}</p>}</div>
          ))}
          {(item.achievements || []).length < 5 && <button type="button" className="resume-secondary" onClick={() => updateArrayItem('experiences', index, 'achievements', [...(item.achievements || []), ''])}>+ Adicionar resultado</button>}
          <p className="resume-truth">Use somente informações verdadeiras. Não invente números ou resultados.</p>
        </div>
      ))}
      {!resume.no_experience && <button type="button" className="resume-add-card" onClick={() => update('experiences', [...resume.experiences, { role: '', company: '', location: '', start_month: '', end_month: '', is_current: false, achievements: ['', ''] }])}>+ Adicionar experiência</button>}
    </>
  );

  const educationStep = (
    <>
      <div className="resume-panel-heading"><span>4</span><div><h2>Formação acadêmica</h2><p>Inclua cursos em andamento e concluídos.</p></div></div>
      {errors.educations && <p className="resume-field-error">{errors.educations}</p>}
      {resume.educations.map((item, index) => (
        <div className="resume-repeat-card" key={item.id || index}>
          <div className="resume-repeat-title"><strong>Formação {index + 1}</strong><button type="button" className="danger" onClick={() => window.confirm('Excluir esta formação?') && update('educations', resume.educations.filter((_, i) => i !== index))}>Excluir</button></div>
          <div className="resume-form-grid">
            <Field label="Nível *"><select value={item.level || ''} onChange={(e) => updateArrayItem('educations', index, 'level', e.target.value)}><option value="">Selecione</option>{LEVELS.map((level) => <option key={level}>{level}</option>)}</select></Field>
            <Field label="Curso *" error={errorFor(`educations.${index}.course`)}><input value={item.course || ''} onChange={(e) => updateArrayItem('educations', index, 'course', e.target.value)} maxLength={FIELD_LIMITS.course} /></Field>
            <Field label="Instituição *" error={errorFor(`educations.${index}.institution`)}><input value={item.institution || ''} onChange={(e) => updateArrayItem('educations', index, 'institution', e.target.value)} maxLength={FIELD_LIMITS.institution} /></Field>
            <Field label="Situação *"><select value={item.status || 'cursando'} onChange={(e) => updateArrayItem('educations', index, 'status', e.target.value)}><option value="cursando">Cursando</option><option value="concluido">Concluído</option><option value="interrompido">Interrompido</option></select></Field>
            <Field label="Início (opcional)"><input type="month" value={item.start_month || ''} onChange={(e) => updateArrayItem('educations', index, 'start_month', e.target.value)} /></Field>
            <Field label={item.status === 'cursando' ? 'Previsão de conclusão' : 'Conclusão'}><input type="month" value={(item.status === 'cursando' ? item.expected_end_month : item.end_month) || ''} onChange={(e) => updateArrayItem('educations', index, item.status === 'cursando' ? 'expected_end_month' : 'end_month', e.target.value)} /></Field>
          </div>
        </div>
      ))}
      <button type="button" className="resume-add-card" onClick={() => update('educations', [...resume.educations, { level: '', course: '', institution: '', status: 'cursando', start_month: '', end_month: '', expected_end_month: '' }])}>+ Adicionar formação</button>
    </>
  );

  const extrasStep = (
    <>
      <div className="resume-panel-heading"><span>5</span><div><h2>Certificações, idiomas e projetos</h2><p>Valorize conhecimentos verificados e experiências complementares.</p></div></div>
      {errors.extras && <p className="resume-field-error" role="alert">{errors.extras}</p>}
      <div className="resume-subsection"><h3>Certificações Abraliber</h3><p className="resume-muted">Dados oficiais, somente leitura. Escolha quais deseja exibir.</p>
        {resume.available_abraliber_certificates.length ? resume.available_abraliber_certificates.map((item) => <label className="resume-certificate-choice" key={item.curso_id}><input type="checkbox" checked={resume.abraliber_certificates.includes(item.curso_id)} onChange={(e) => update('abraliber_certificates', e.target.checked ? [...resume.abraliber_certificates, item.curso_id] : resume.abraliber_certificates.filter((id) => id !== item.curso_id))} /><span><strong>{item.name}</strong><small>Abraliber | {item.year || 'Ano não informado'}{item.hours ? ` | ${item.hours} horas` : ''}</small></span></label>) : <p className="resume-empty">Nenhum certificado Abraliber elegível para esta conta.</p>}
      </div>
      <div className="resume-subsection"><h3>Outras certificações</h3>
        {resume.external_certifications.map((item, index) => (
          <div className="resume-repeat-card compact" key={item.id || index}>
            <button type="button" className="resume-remove-corner" onClick={() => update('external_certifications', resume.external_certifications.filter((_, i) => i !== index))}>×</button>
            <div className="resume-form-grid">
              <Field label="Certificação *" error={errorFor(`external_certifications.${index}.name`)}><input value={item.name || ''} onChange={(e) => updateArrayItem('external_certifications', index, 'name', e.target.value)} maxLength={FIELD_LIMITS.certification} /></Field>
              <Field label="Instituição *" error={errorFor(`external_certifications.${index}.institution`)}><input value={item.institution || ''} onChange={(e) => updateArrayItem('external_certifications', index, 'institution', e.target.value)} maxLength={FIELD_LIMITS.institution} /></Field>
              <Field label="Ano *"><input type="number" min="1950" max="2036" value={item.year || ''} onChange={(e) => updateArrayItem('external_certifications', index, 'year', e.target.value)} /></Field>
              <Field label="Carga horária"><input type="number" min="1" max="10000" value={item.hours || ''} onChange={(e) => updateArrayItem('external_certifications', index, 'hours', e.target.value)} /></Field>
              <Field label="Código da credencial" error={errorFor(`external_certifications.${index}.credential_code`)}><input value={item.credential_code || ''} onChange={(e) => updateArrayItem('external_certifications', index, 'credential_code', e.target.value)} maxLength={FIELD_LIMITS.credentialCode} /></Field>
              <Field label="URL de validação" error={errorFor(`external_certifications.${index}.validation_url`)}><input type="url" value={item.validation_url || ''} onChange={(e) => updateArrayItem('external_certifications', index, 'validation_url', e.target.value)} maxLength={FIELD_LIMITS.url} /></Field>
            </div>
          </div>
        ))}
        <button type="button" className="resume-secondary" onClick={() => update('external_certifications', [...resume.external_certifications, { name: '', institution: '', year: new Date().getFullYear(), hours: '', credential_code: '', validation_url: '' }])}>+ Adicionar certificação</button>
      </div>
      <div className="resume-subsection"><h3>Idiomas</h3>
        {errorFor('languages') && <p className="resume-field-error">{errorFor('languages')}</p>}
        {resume.languages.map((item, index) => <div className="resume-inline-add" key={item.id || index}><input aria-label="Idioma" value={item.language || ''} onChange={(e) => updateArrayItem('languages', index, 'language', e.target.value)} placeholder="Idioma" maxLength={FIELD_LIMITS.language} /><select aria-label="Nível" value={item.level || 'basico'} onChange={(e) => updateArrayItem('languages', index, 'level', e.target.value)}>{LANGUAGE_LEVELS.map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select><button type="button" className="danger" onClick={() => update('languages', resume.languages.filter((_, i) => i !== index))}>Remover</button></div>)}
        {resume.languages.map((item, index) => errorFor(`languages.${index}.language`) && <p className="resume-field-error" key={`language-error-${index}`}>{errorFor(`languages.${index}.language`)}</p>)}
        <button type="button" className="resume-secondary" onClick={() => update('languages', [...resume.languages, { language: '', level: 'basico' }])}>+ Adicionar idioma</button>
      </div>
      <div className="resume-subsection"><h3>Projetos e trabalhos voluntários</h3>
        {resume.projects.map((item, index) => (
          <div className="resume-repeat-card compact" key={item.id || index}>
            <div className="resume-repeat-title"><strong>Projeto {index + 1}</strong><button type="button" className="danger" onClick={() => update('projects', resume.projects.filter((_, i) => i !== index))}>Excluir</button></div>
            <div className="resume-form-grid">
              <Field label="Projeto ou organização *" error={errorFor(`projects.${index}.name`)}><input value={item.name || ''} onChange={(e) => updateArrayItem('projects', index, 'name', e.target.value)} maxLength={FIELD_LIMITS.projectName} /></Field>
              <Field label="Função *" error={errorFor(`projects.${index}.role`)}><input value={item.role || ''} onChange={(e) => updateArrayItem('projects', index, 'role', e.target.value)} maxLength={FIELD_LIMITS.projectRole} /></Field>
              <Field label="Início"><input type="month" value={item.start_month || ''} onChange={(e) => updateArrayItem('projects', index, 'start_month', e.target.value)} /></Field>
              <Field label="Término"><input type="month" disabled={item.is_current} value={item.end_month || ''} onChange={(e) => updateArrayItem('projects', index, 'end_month', e.target.value)} /></Field>
              <Field label="Link" error={errorFor(`projects.${index}.link`)}><input type="url" value={item.link || ''} onChange={(e) => updateArrayItem('projects', index, 'link', e.target.value)} maxLength={FIELD_LIMITS.url} /></Field>
              <label className="resume-check align-end"><input type="checkbox" checked={Boolean(item.is_current)} onChange={(e) => updateArrayItem('projects', index, 'is_current', e.target.checked)} /> Participo atualmente</label>
            </div>
            <Field label="Atividades e resultados" error={errorFor(`projects.${index}.activities`)}><textarea rows="3" value={item.activities || ''} onChange={(e) => updateArrayItem('projects', index, 'activities', e.target.value)} maxLength={FIELD_LIMITS.projectActivities} /></Field>
          </div>
        ))}
        <button type="button" className="resume-secondary" onClick={() => update('projects', [...resume.projects, { name: '', role: '', start_month: '', end_month: '', is_current: false, activities: '', link: '' }])}>+ Adicionar projeto</button>
      </div>
    </>
  );

  const reviewStep = (
    <>
      <div className="resume-panel-heading"><span>6</span><div><h2>Revisão e geração</h2><p>Revise o currículo e escolha os formatos de download.</p></div></div>
      <div className="resume-review-list">{STEPS.slice(0, 5).map((label, index) => <div key={label}><span className={sectionsComplete[index] ? 'complete' : 'warning'}>{sectionsComplete[index] ? '✓' : '!'}</span><strong>{label}</strong><button type="button" onClick={() => setStep(index + 1)}>Editar</button></div>)}</div>
      <div className="resume-subsection"><h3>Seções opcionais</h3>
        {['certifications', 'languages', 'projects'].map((key) => {
          const labels = { certifications: 'Certificações', languages: 'Idiomas', projects: 'Projetos' };
          const hidden = resume.settings.hidden_sections.includes(key);
          const index = resume.settings.optional_order.indexOf(key);
          return <div className="resume-option-row" key={key}><label className="resume-check"><input type="checkbox" checked={!hidden} onChange={(e) => update('settings', { ...resume.settings, hidden_sections: e.target.checked ? resume.settings.hidden_sections.filter((item) => item !== key) : [...resume.settings.hidden_sections, key] })} /> Exibir {labels[key]}</label><div><button type="button" onClick={() => update('settings', { ...resume.settings, optional_order: move(resume.settings.optional_order, index, -1) })}>↑</button><button type="button" onClick={() => update('settings', { ...resume.settings, optional_order: move(resume.settings.optional_order, index, 1) })}>↓</button></div></div>;
        })}
      </div>
      <label className="resume-confirm"><input type="checkbox" checked={Boolean(resume.settings.truthful_confirmed)} onChange={(e) => update('settings', { ...resume.settings, truthful_confirmed: e.target.checked })} /><span><strong>Confirmo que as informações são verdadeiras.</strong><small>Revise nomes, datas e resultados antes de gerar o currículo.</small></span></label>
      <div className="resume-downloads"><button type="button" disabled={busy} onClick={() => download('pdf')}>Gerar e baixar PDF</button><button type="button" disabled={busy} onClick={() => download('docx')}>Gerar e baixar DOCX</button></div>
      {resume.updated_at && <p className="resume-muted">Última atualização: {new Date(resume.updated_at).toLocaleString('pt-BR')}</p>}
    </>
  );

  const stepContent = [personalStep, summaryStep, experienceStep, educationStep, extrasStep, reviewStep][step - 1];

  return (
    <main className="resume-builder-page">
      <div className="resume-builder-top">
        <div><span className="resume-eyebrow">Ferramenta administrativa</span><h1>Criar currículo profissional</h1></div>
        <div className="resume-version-actions">
          <select aria-label="Versão do currículo" value={resume.id || ''} onChange={(e) => openResume(Number(e.target.value))}>{resumes.map((item) => <option value={item.id} key={item.id}>{item.title}{item.is_primary ? ' — principal' : ''}</option>)}</select>
          <button type="button" onClick={createNew} disabled={busy}>Novo</button>
          <button type="button" onClick={duplicate} disabled={busy || !resume.id}>Criar versão</button>
          {!resume.is_primary && <button type="button" onClick={makePrimary}>Definir principal</button>}
          <button type="button" className="danger" onClick={removeResume} disabled={busy || !resume.id}>Excluir</button>
          <button type="button" className="resume-save-draft" onClick={() => saveNow(resume, true)} disabled={busy || !resume.id}>Salvar rascunho</button>
        </div>
      </div>

      <nav className="resume-progress" aria-label="Etapas do currículo">
        {STEPS.map((label, index) => <button type="button" key={label} className={step === index + 1 ? 'active' : step > index + 1 ? 'done' : ''} onClick={() => setStep(index + 1)}><span>{index + 1}</span><small>{label}</small></button>)}
      </nav>

      {notice && <div className="resume-notice" role="status"><span>{notice}</span><button type="button" aria-label="Fechar aviso" onClick={() => setNotice('')}>×</button></div>}

      <div className="resume-builder-grid">
        <section className="resume-form-panel" aria-busy={!ready || busy}>
          {!ready ? <div className="resume-loading">Carregando seu currículo...</div> : stepContent}
          {ready && <div className="resume-form-actions"><button type="button" className="resume-back" disabled={step === 1} onClick={() => setStep((current) => Math.max(1, current - 1))}>Voltar</button><span className={`resume-save-state ${saveState.includes('Falha') ? 'error' : ''}`}>{saveState}</span>{step < 6 ? <button type="button" className="resume-continue" onClick={nextStep}>Salvar e continuar</button> : <button type="button" className="resume-continue" onClick={() => download('pdf')} disabled={busy}>Gerar meu currículo</button>}</div>}
        </section>
        <aside className="resume-preview-column">
          <div className="resume-live-label"><span /> Prévia em tempo real</div>
          <ResumePreview resume={resume} />
        </aside>
      </div>

      <button type="button" className="resume-mobile-preview" onClick={() => setPreviewOpen(true)}>Visualizar currículo</button>
      {previewOpen && <div className="resume-preview-modal" role="dialog" aria-modal="true" aria-label="Prévia do currículo"><button type="button" className="resume-modal-close" onClick={() => setPreviewOpen(false)}>Fechar prévia ×</button><ResumePreview resume={resume} /></div>}
    </main>
  );
};

export default ResumeBuilder;
